import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowUpRight, Minus, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { CareMark } from '@components/common/Brand';
import { Button } from '@components/common/Button';
import { ProductMedia } from '@components/product/ProductMedia';
import { PaymentNotice } from '@components/product/ProductPurchase';
import { useCart } from '@context/CartContext';
import { checkoutPath } from '@services/checkout/checkoutIntent';
import { TaxExclusion } from '@components/product/Price';
import { formatCurrencyMinor } from '@utils/formatCurrency';
import { VAT_RATE_PERCENT } from '@utils/money';
export const CartDrawer = () => {
    const { t, i18n } = useTranslation();
    const { items, subtotalMinor, vatMinor, totalMinor, isOpen, closeCart, removeProduct, setQuantity, clearCart } = useCart();
    const closeButtonRef = useRef(null);
    const drawerRef = useRef(null);
    const navigate = useNavigate();
    useEffect(() => {
        if (!isOpen)
            return;
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        const background = Array.from(drawerRef.current?.parentElement?.children ?? []).filter((element) => element instanceof HTMLElement && element.matches('header, main, footer'));
        const previousInert = background.map((element) => element.inert);
        background.forEach((element) => {
            element.inert = true;
        });
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        const handleKey = (event) => {
            if (event.key === 'Escape')
                closeButtonRef.current?.click();
            if (event.key !== 'Tab')
                return;
            const controls = drawerRef.current?.querySelectorAll('button:not(:disabled), a[href], input, select, textarea, [tabindex="0"]');
            if (!controls?.length)
                return;
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (!drawerRef.current?.contains(document.activeElement)) {
                event.preventDefault();
                first.focus();
            }
            else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => {
            background.forEach((element, index) => {
                element.inert = previousInert[index];
            });
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKey);
            previousFocus?.focus();
        };
    }, [isOpen]);
    const variantLabel = (months) => months ? t('personalCare360.duration', { months }) : '';
    const buy = (lineId) => {
        const line = items.find((item) => item.lineId === lineId);
        if (!line)
            return;
        closeCart();
        navigate(checkoutPath(line.product, line.variant?.id));
    };
    return (_jsxs(_Fragment, { children: [_jsx(Backdrop, { "$isOpen": isOpen, onClick: closeCart, "aria-hidden": "true" }), _jsxs(Drawer, { ref: drawerRef, role: "dialog", "aria-modal": "true", "aria-labelledby": "cart-title", "$isOpen": isOpen, children: [_jsxs(DrawerHeader, { children: [_jsxs("div", { children: [_jsxs(CartEyebrow, { children: ["AITechCare / ", t('interior.cart.selection')] }), _jsx("h2", { id: "cart-title", children: t('cart.title') })] }), _jsx(IconButton, { ref: closeButtonRef, type: "button", "aria-label": t('cart.close'), onClick: closeCart, children: _jsx(X, { size: 21, "aria-hidden": "true" }) })] }), items.length === 0 ? (_jsxs(EmptyState, { children: [_jsx(CareMark, {}), _jsx("h3", { children: t('cart.empty') }), _jsx("p", { children: t('interior.cart.emptyText') }), _jsxs(Button, { type: "button", onClick: closeCart, children: [t('actions.continueBrowsing'), _jsx(ArrowUpRight, { size: 17, "aria-hidden": "true" })] }), _jsx(CatalogLink, { to: "/products", onClick: closeCart, children: t('actions.viewProducts') })] })) : (_jsxs(CartContent, { children: [_jsx(ItemsList, { children: items.map(({ product, variant, lineId, quantity, totalMinor: lineTotalMinor }) => (_jsxs(CartLine, { "aria-labelledby": `cart-product-${lineId}${variant ? ` cart-duration-${lineId}` : ''}`, children: [_jsxs(LineTop, { children: [_jsx(Thumb, { product: product, eager: true }), _jsxs(LineHeading, { children: [_jsx("span", { children: t(`productsPage.categories.${product.category}`) }), _jsx("h3", { id: `cart-product-${lineId}`, children: _jsx(Link, { to: `/products/${product.slug}`, onClick: closeCart, children: t(product.nameKey) }) }), variant && (_jsx(Duration, { id: `cart-duration-${lineId}`, children: t('personalCare360.selected', {
                                                                duration: variantLabel(variant.durationMonths)
                                                            }) })), _jsxs("p", { children: [variant ? t('personalCare360.periodTotal') : t('interior.cart.unitPrice'), ' ', _jsxs("strong", { children: [formatCurrencyMinor(product.priceMinor, product.currency, i18n.language), ' ', _jsx(TaxExclusion, { short: true })] })] })] }), _jsx(RemoveButton, { type: "button", "aria-label": `${t('actions.remove')}: ${t(product.nameKey)} ${variantLabel(variant?.durationMonths)}`, onClick: () => {
                                                        removeProduct(lineId);
                                                        closeButtonRef.current?.focus();
                                                    }, children: _jsx(Trash2, { size: 18, "aria-hidden": "true" }) })] }), _jsxs(QuantityRow, { children: [_jsxs(QuantityGroup, { children: [_jsx("span", { children: t('cart.quantity') }), _jsxs(QuantityControls, { children: [_jsx(IconButton, { type: "button", "aria-label": `${t('cart.decrease')}: ${t(product.nameKey)} ${variantLabel(variant?.durationMonths)}`, onClick: () => {
                                                                        setQuantity(lineId, quantity - 1);
                                                                        if (quantity === 1)
                                                                            closeButtonRef.current?.focus();
                                                                    }, children: _jsx(Minus, { size: 16, "aria-hidden": "true" }) }), _jsx(QuantityValue, { "aria-live": "polite", children: quantity }), _jsx(IconButton, { type: "button", "aria-label": `${t('cart.increase')}: ${t(product.nameKey)} ${variantLabel(variant?.durationMonths)}`, onClick: () => setQuantity(lineId, quantity + 1), children: _jsx(Plus, { size: 16, "aria-hidden": "true" }) })] })] }), _jsxs(LineTotal, { children: [_jsx("span", { children: t('interior.cart.lineTotal') }), _jsx("strong", { children: formatCurrencyMinor(lineTotalMinor, product.currency, i18n.language) }), _jsx(TaxExclusion, { short: true })] })] }), _jsxs(LineBuy, { children: [_jsxs(Button, { type: "button", onClick: () => buy(lineId), children: [t('actions.buyNow'), _jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" })] }), _jsx(PaymentNotice, { product: product })] })] }, lineId))) }), _jsxs(Summary, { "aria-label": t('cart.summary'), children: [_jsxs(SummaryBreakdown, { "aria-live": "polite", "aria-atomic": "true", children: [_jsxs(SummaryRow, { children: [_jsx("span", { children: t('tax.subtotalExcluding') }), _jsx("strong", { children: formatCurrencyMinor(subtotalMinor, 'EUR', i18n.language) })] }), _jsxs(SummaryRow, { children: [_jsx("span", { children: t('tax.vatRate', { rate: VAT_RATE_PERCENT }) }), _jsx("strong", { children: formatCurrencyMinor(vatMinor, 'EUR', i18n.language) })] }), _jsxs(TotalRow, { children: [_jsx("span", { children: t('tax.totalIncluding') }), _jsx("strong", { children: formatCurrencyMinor(totalMinor, 'EUR', i18n.language) })] })] }), _jsx(Note, { children: t('interior.cart.checkoutNote') }), _jsxs(SummaryActions, { children: [_jsx(QuietButton, { type: "button", onClick: () => {
                                                    clearCart();
                                                    closeButtonRef.current?.focus();
                                                }, children: t('actions.emptyCart') }), _jsx(QuietButton, { type: "button", onClick: closeCart, children: t('actions.continueBrowsing') })] })] })] }))] })] }));
};
const enter = keyframes `from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; }`;
const Backdrop = styled.div `
  position: fixed;
  inset: 0;
  z-index: 30;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  background: rgba(23, 26, 46, 0.35);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 180ms ease;
`;
const Drawer = styled.aside `
  position: fixed;
  top: 0;
  right: 0;
  z-index: 40;
  display: ${({ $isOpen }) => ($isOpen ? 'grid' : 'none')};
  grid-template-rows: auto minmax(0, 1fr);
  width: min(100vw, 480px);
  height: 100vh;
  height: 100dvh;
  background: ${({ theme }) => theme.colors.white};
  border-left: 1px solid ${({ theme }) => theme.colors.line};
  animation: ${enter} 200ms ease-out;
`;
const DrawerHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.navy900};
  background: ${({ theme }) => theme.colors.surface};
  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: -0.04em;
  }
  @media (max-width: 374px) {
    padding-inline: 1.25rem;
  }
`;
const CartEyebrow = styled.p `
  margin: 0 0 0.4rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.6875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;
const IconButton = styled.button `
  display: inline-flex;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.navy900};
  background: transparent;
  &:hover {
    background: ${({ theme }) => theme.colors.mint50};
  }
`;
const EmptyState = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 0;
  overflow-y: auto;
  padding: 2rem;
  gap: 1.25rem;
  > svg {
    width: 52px;
    height: 52px;
    color: ${({ theme }) => theme.colors.navy900};
  }
  h3 {
    margin: 0;
    max-width: 320px;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.8rem;
    font-weight: 400;
    letter-spacing: -0.04em;
    line-height: 1.2;
  }
  p {
    margin: 0 0 0.5rem;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.75;
  }
  @media (max-height: 500px) {
    justify-content: flex-start;
  }
`;
const CatalogLink = styled(Link) `
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.9375rem;
  text-decoration: underline;
  text-underline-offset: 4px;
`;
const CartContent = styled.div `
  display: grid;
  min-height: 0;
  grid-template-rows: minmax(0, 1fr) auto;
`;
const ItemsList = styled.div `
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 1.5rem;
  @media (max-width: 374px) {
    padding-inline: 1.25rem;
  }
`;
const CartLine = styled.article `
  padding-block: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  &:last-child {
    border-bottom: 0;
  }
`;
const LineTop = styled.div `
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) 44px;
  align-items: start;
  gap: 0.8rem;
  @media (max-width: 374px) {
    grid-template-columns: 60px minmax(0, 1fr) 44px;
    gap: 0.5rem;
  }
`;
const Thumb = styled(ProductMedia) `
  padding-top: 0.25rem;
`;
const LineHeading = styled.div `
  min-width: 0;
  > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  h3 {
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: break-word;
  }
  h3 a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }
  h3 a:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  p {
    margin: 0.35rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
    line-height: 1.6;
  }
  p strong {
    font-weight: 400;
    white-space: nowrap;
  }
`;
const RemoveButton = styled(IconButton) `
  border-color: transparent;
`;
const QuantityRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 1rem;
  margin-top: 1rem;
`;
const QuantityGroup = styled.div `
  > span {
    display: block;
    margin-bottom: 0.35rem;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
  }
`;
const QuantityControls = styled.div `
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 6px;
  ${IconButton} {
    border: 0;
  }
`;
const QuantityValue = styled.span `
  min-width: 30px;
  padding-inline: 0.2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.9375rem;
  font-variant-numeric: tabular-nums;
`;
const LineTotal = styled.div `
  display: grid;
  gap: 0.5rem;
  padding-bottom: 0.65rem;
  text-align: right;
  span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
  }
  strong {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.125rem;
    white-space: nowrap;
  }
`;
const LineBuy = styled.div `
  margin-top: 1rem;
  > button {
    width: 100%;
    min-height: 44px;
    font-size: 0.875rem;
  }
`;
const Summary = styled.div `
  padding: 1.25rem 1.5rem 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  @media (max-width: 374px) {
    padding-inline: 1.25rem;
  }
`;
const SummaryBreakdown = styled.div `
  display: grid;
  gap: 0.55rem;
`;
const SummaryRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  min-width: 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.8125rem;
  line-height: 1.4;
  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  strong {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;
const TotalRow = styled(SummaryRow) `
  margin-top: 0.15rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 0.75rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 1rem;
  font-weight: 700;
  strong {
    font-size: 1.4rem;
    font-weight: 700;
  }
`;
const Note = styled.p `
  margin: 0.6rem 0 0.25rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.75rem;
  line-height: 1.6;
`;
const SummaryActions = styled.div `
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0 1rem;
`;
const QuietButton = styled.button `
  min-height: 44px;
  padding: 0.5rem 0;
  border: 0;
  color: ${({ theme }) => theme.colors.navy900};
  background: transparent;
  font-size: 0.8125rem;
  text-decoration: underline;
  text-underline-offset: 4px;
`;
const Duration = styled.p `
  color: ${({ theme }) => theme.colors.navy900} !important;
  font-weight: 700;
`;
